import { SelectionCommand } from './commands';
import { useReducer } from 'react';
import { FINISHED } from '../hooks/constants';
import { NormalizedContentAssistanceResult } from '../api/contentAssistanceApi';
import { Action } from '../utils/types';

const SET_SELECTED_COMMAND = 'SET_SELECTED_COMMAND';
const SHOW_RESULT = 'SHOW_RESULT';
const INCREMENT_CLICK_COUNT = 'INCREMENT_CLICK_COUNT';
const SET_GENERATED_CONTENT = 'SET_GENERATED_CONTENT';
const PRESERVE_EXTRA_PARAMS = 'PRESERVE_EXTRA_PARAMS';

type InlineToolbarState = {
  regenerateClickCount: number; // max clicks is 5
  selectedCommand?: SelectionCommand | null;
  generatedContent?: NormalizedContentAssistanceResult | null;
  allResults: NormalizedContentAssistanceResult[];
  selectedResultIndex: number;
  previousExtraParams: Record<string, unknown>;
};

const initialState: InlineToolbarState = {
  regenerateClickCount: 0, // max clicks is 5
  selectedCommand: null,
  generatedContent: null,
  allResults: [],
  selectedResultIndex: 0,
  previousExtraParams: {},
};

type FinishedPayload = {
  response: NormalizedContentAssistanceResult[];
  generatedContent: NormalizedContentAssistanceResult;
};
type GeneratedContentPayload = {
  generatedContent: NormalizedContentAssistanceResult;
};
type SelectedCommandPayload = {
  selectedCommand: SelectionCommand;
};
type ShowResultPayload = {
  selectedResultIndex: number;
};

type PreserveExtraParamsPayload = {
  extraParams: Record<string, unknown>;
};

type ActionOverload =
  | Action<typeof SET_SELECTED_COMMAND, SelectedCommandPayload>
  | Action<typeof SHOW_RESULT, ShowResultPayload>
  | Action<typeof INCREMENT_CLICK_COUNT>
  | Action<typeof SET_GENERATED_CONTENT, GeneratedContentPayload>
  | Action<typeof FINISHED, FinishedPayload>
  | Action<typeof PRESERVE_EXTRA_PARAMS, PreserveExtraParamsPayload>;

const reducer = (
  state: InlineToolbarState,
  action: ActionOverload
): InlineToolbarState => {
  const { type, payload } = action;
  switch (type) {
    case INCREMENT_CLICK_COUNT:
      return {
        ...state,
        regenerateClickCount: state.regenerateClickCount + 1,
      };
    case FINISHED:
      return {
        ...state,
        allResults: [...state.allResults, ...payload.response],
        // Reset index, and content shown in preview
        selectedResultIndex:
          state.allResults.length + payload.response.length - 1,
        generatedContent: payload.generatedContent,
      };
    case SET_GENERATED_CONTENT:
      return {
        ...state,
        generatedContent: payload.generatedContent,
      };
    case SHOW_RESULT:
      return {
        ...state,
        selectedResultIndex: payload.selectedResultIndex,
        generatedContent: state.allResults[payload.selectedResultIndex],
      };
    case SET_SELECTED_COMMAND:
      return {
        ...state,
        selectedCommand: payload.selectedCommand,
      };
    case PRESERVE_EXTRA_PARAMS:
      return {
        ...state,
        previousExtraParams: payload.extraParams,
      };
    default:
      console.error('Unexpected action type ', type);
      return state;
  }
};
export const useInlineToolbarState = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // TODO: Wrap these in `useCallback`s
  const setGeneratedContent = (
    generatedContent: NormalizedContentAssistanceResult
  ) => {
    dispatch({
      type: SET_GENERATED_CONTENT,
      payload: {
        generatedContent,
      },
    });
  };
  const onSuccess = (response: NormalizedContentAssistanceResult[]) =>
    dispatch({
      type: FINISHED,
      payload: { response, generatedContent: response[0] },
    });

  const setSelectedCommand = (command: SelectionCommand) =>
    dispatch({
      type: SET_SELECTED_COMMAND,
      payload: {
        selectedCommand: command,
      },
    });

  const incrementCount = () =>
    dispatch({
      type: INCREMENT_CLICK_COUNT,
    });

  const setShowResult = (selectedResultIndex: number) =>
    dispatch({ type: SHOW_RESULT, payload: { selectedResultIndex } });
  const setExtraParams = (extraParams: Record<string, unknown>) =>
    dispatch({ type: PRESERVE_EXTRA_PARAMS, payload: { extraParams } });

  return {
    state,
    actions: {
      onSuccess,
      setGeneratedContent,
      setSelectedCommand,
      incrementCount,
      setShowResult,
      setExtraParams,
    },
  };
};
